import { useLayoutEffect, useReducer, useMemo } from 'react'

/**
 * Find the theme applied to a given element. The naming convention of
 * themes within the SDK is quite simplistic so it might returns themes
 * that are not part of the SDK.
 *
 * @param {Element} element
 * @returns {string}
 */
export function findTheme (element) {
  while ((element = element.parentElement)) {
    if (element.className) {
      let theme = /(?:^|\s+)([^\s]+-theme)(?:\s+|$)/.exec(element.className)

      if (theme) {
        return theme[1]
      }
    }
  }

  return ''
}

/**
 * @template [Element=HTMLElement]
 * @template [Props=Record<string, any>]
 * @param {import('react').MutableRefObject<Element & Props & {element: Element}>} ref
 * @param {import('react').MutableRefObject<Element>} elementRef
 * @param {Props} elementProps
 */
export function useUpdateRef (ref, elementRef, elementProps) {
  return useLayoutEffect(elementDidMount, [elementRef.current, elementProps])

  function updateRef (element) {
    if (typeof ref === 'function') {
      ref(element)
    } else if (ref && 'current' in ref) {
      ref.current = element
    }
  }

  function elementDidMount () {
    const element = elementRef.current
    updateRef(
      element && elementProps
        ? { element, ...element, ...elementProps }
        : element
    )
    return elementWillUnmount
  }

  function elementWillUnmount () {
    updateRef(null)
  }
}

/**
 * @template A, S
 * @type {import('./interfaces').useSetState<A, S>}
 */
export function useSetState (defaultState, init) {
  return useReducer(stateReducer, defaultState, init)
}

/**
 * @template A, S
 * @type {import('./interfaces').stateReducer<A, S>}
 */
function stateReducer (state, action) {
  return Object.keys(action).some(
    key => !(key in state) || state[key] !== action[key]
  )
    ? { ...state, ...action }
    : state
}

/**
 * @param {Node} element
 * @returns {string}
 */
export function useTheme (element) {
  return useMemo(() => (element && findTheme(element)) || '', [element])
}

let globalId = 0
/**
 * @param {string} prefix
 * @returns {string}
 */
export function useGlobalId (prefix) {
  return useMemo(() => `${prefix || ''}${globalId++}`, [prefix])
}

/**
 * Create a state with a way to toggle it, or set it.
 * @param {boolean} initial Initial state value
 * @returns {[boolean, import('react').DispatchWithoutAction | import('react').Dispatch<boolean> | import('react').Dispatch<any>]}
 */
export function useToggle (initial) {
  return useReducer((state, override) => typeof override === 'boolean' ? override : !state, initial)
}
